import {gsap} from "gsap";
import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../core/winpresentation/SimplePresentationRunner";
import {BrickSnake2000} from "../../BrickSnake2000";
import {BrickSnake2000WildSymbol} from "../../reels/symbols/WildSymbol";
import {SymbolArgs} from "../../reels/symbols/BrickSnake2000Symbol";

interface reelPosition {
    reelPosition:number,
    reelID:number,
}

export class MoveSnakes implements SimplePresentation {

    name:string = "RevealXWays";
    private snakeSoundIndex:number;

    constructor() {
        this.snakeSoundIndex = 0;
    }

    shouldRun():boolean {
        return BrickSnake2000.model.gameData.snakes.length > 0;
    }

    start(controller:SimplePresentationRunnerController):void {
        const tl = gsap.timeline({
            onStart: () => {
                controller.setSkipCallback(() => {tl.seek("End", false)});
            },
            onComplete: () => {
                controller.setSkipCallback();
                controller.finish()
            }
        });
        const snakeData = BrickSnake2000.model.getBaseGameSnakeData();
        //snakeData.slice(1).forEach((pos, index) => {
        for (let i = 1; i < snakeData.length; i++) {
            const prevPos = snakeData[i - 1];
            const pos = snakeData[i];

            const isPrevTurned = i > 1 && ((snakeData[i - 2].reelPosition != snakeData[i].reelPosition) &&
                (snakeData[i - 2].reelID != snakeData[i].reelID));
            const prevSymbolNewPart = i == 1
                ? "_Tail"
                : isPrevTurned ? "_Knee" : "_Body";

            const prevRotation = isPrevTurned
                ? MoveSnakes.getTurnedRotation(snakeData[i - 2], prevPos, pos)
                : MoveSnakes.getRotation(prevPos, pos);
            const rotation = MoveSnakes.getRotation(prevPos, pos);
            const cameFromDirection = MoveSnakes.getPreviousDirection(prevPos, pos);

            const symbolSize     = BrickSnake2000.model.data.symbolSizes[pos.reelID][pos.reelPosition];
            const prevSymbolSize = BrickSnake2000.model.data.symbolSizes[prevPos.reelID][prevPos.reelPosition];

            const brokeWall = MoveSnakes.brokeWall(prevPos, pos);

            const symbolArgs:SymbolArgs = {
                symbolName: "W",
                isSpinning: false,
                isStopping: false,
                reelsView: BrickSnake2000.baseGameReelView,
                reelPosition: pos.reelPosition,
                reelID: pos.reelID,
                symbolSize: symbolSize
            };
            tl.add(() => {
                BrickSnake2000.soundPlayer.playSound((this.snakeSoundIndex++ % 2 == 0) ? "snekMove1" : "snekMove2");
                if (symbolArgs.symbolSize > 1)
                    BrickSnake2000.soundPlayer.playSound("xwaysEat");
                const newSymbol = BrickSnake2000.symbolFactory.createSymbol(Object.assign({suffix: "_Head"}, symbolArgs));
                (<BrickSnake2000WildSymbol>newSymbol).setRotation(rotation, cameFromDirection);
                BrickSnake2000.baseGameReelView.replaceSymbolAt(pos.reelID, pos.reelPosition, newSymbol);

                const newPrevSymbol = BrickSnake2000.symbolFactory.createSymbol(Object.assign({suffix: prevSymbolNewPart},
                    symbolArgs,
                    {symbolSize: prevSymbolSize}));
                (<BrickSnake2000WildSymbol>newPrevSymbol).setRotation(prevRotation);
                BrickSnake2000.baseGameReelView.replaceSymbolAt(prevPos.reelID, prevPos.reelPosition, newPrevSymbol);
            }, '>0.4')
            if (brokeWall) {
                tl.add(BrickSnake2000.baseGameReelView.doWallExplosion(prevPos, pos));
                tl.add(() => {BrickSnake2000.soundPlayer.playSound("snekThroughWall")}, '<');
            }
        }
        //})
        //})
        tl.addLabel("End")
        tl.add(() => {}, '>0.3');
    }

    public static getPreviousDirection(prevPosition:reelPosition, currentPosition:reelPosition) {
        if (prevPosition.reelID == currentPosition.reelID) {
            if ((prevPosition.reelPosition == 4 && currentPosition.reelPosition == 0) ||
                prevPosition.reelPosition < currentPosition.reelPosition) {
                return "down";
            } else {
                return "up"
            }
        } else {
            if ((prevPosition.reelID == 0 && currentPosition.reelID == 4) ||
                prevPosition.reelID > currentPosition.reelID) {
                return "right";
            } else {
                return "left"
            }
        }

    }

    public static isAbove(a:reelPosition, b:reelPosition) {
        return (a.reelPosition == 4 && b.reelPosition == 0) || (a.reelPosition + 1 == b.reelPosition)
    };

    public static isBelow(a:reelPosition, b:reelPosition) {
        return (a.reelPosition == 0 && b.reelPosition == 4) || (a.reelPosition - 1 == b.reelPosition);
    };

    public static isLeftOf(a:reelPosition, b:reelPosition) {
        return (a.reelID == 4 && b.reelID == 0) || (a.reelID + 1 == b.reelID);
    };

    public static isRightOf(a:reelPosition, b:reelPosition) {
        return (a.reelID == 0 && b.reelID == 4) || (a.reelID - 1 == b.reelID);
    };

    // TODO break out the types here :upside_down_face:
    public static getTurnedRotation(prevPrevPos:reelPosition, prevPos:reelPosition, pos:reelPosition):number {
        return   ((MoveSnakes.isBelow(prevPrevPos, prevPos) && MoveSnakes.isLeftOf(pos, prevPos)) ||
                  (MoveSnakes.isBelow(pos, prevPos)         && MoveSnakes.isLeftOf(prevPrevPos, prevPos)))
               ? 0
               : ((MoveSnakes.isAbove(prevPrevPos, prevPos) && MoveSnakes.isLeftOf(pos, prevPos)) ||
                  (MoveSnakes.isAbove(pos, prevPos)         && MoveSnakes.isLeftOf(prevPrevPos, prevPos)))
               ? Math.PI * 0.5
               : ((MoveSnakes.isAbove(prevPrevPos, prevPos) && MoveSnakes.isRightOf(pos, prevPos)) ||
                  (MoveSnakes.isAbove(pos, prevPos)         && MoveSnakes.isRightOf(prevPrevPos, prevPos)))
               ? Math.PI
               : Math.PI * 1.5;


    }

    public static getRotation(prevPos:reelPosition, pos:reelPosition):number {
        //type reelPosition = {reelPosition:number, reelID:number};
        const isAbove   = (a:reelPosition, b:reelPosition) => (a.reelPosition == 4 && b.reelPosition == 0) ||
            (a.reelPosition + 1 == b.reelPosition);
        const isBelow   = (a:reelPosition, b:reelPosition) => (a.reelPosition == 0 && b.reelPosition == 4) ||
            (a.reelPosition - 1 == b.reelPosition);
        const isLeftOf  = (a:reelPosition, b:reelPosition) => (a.reelID == 4 && b.reelID == 0) ||
            (a.reelID + 1 == b.reelID);
        const isRightOf = (a:reelPosition, b:reelPosition) => (a.reelID == 0 && b.reelID == 4) ||
            (a.reelID - 1 == b.reelID);

        return   isAbove(prevPos, pos)
               ? Math.PI * 0.5
               : isBelow(prevPos, pos)
               ? Math.PI * 1.5
               : isLeftOf(prevPos, pos)
               ? 0
               : Math.PI;
    }

    public static brokeWall(prevPos:reelPosition, pos:reelPosition):boolean {
        return ((prevPos.reelID == pos.reelID) && ((prevPos.reelPosition == 0 && pos.reelPosition == 4) ||
                                                   (prevPos.reelPosition == 4 && pos.reelPosition == 0))) ||
               ((prevPos.reelPosition == pos.reelPosition) && ((prevPos.reelID == 0 && pos.reelID == 4) ||
                                                               (prevPos.reelID == 4 && pos.reelID == 0)));
    }

}
